import React from 'react'
import {
  CustomColumnHeaderRendererProps,
  doFiltersHaveOptions,
  doFiltersHaveSelectionsOnGroups
} from '../../legacy-design-system/grid'
import { Filters, FilterConfig } from '../../legacy-design-system/filter'
import {
  FilterConfigs,
  FilterSelections
} from '../../legacy-design-system/filter/filters'
import { SessionMetadataAggregation } from '../session-metadata-aggregation'
import { MetadataFilter } from '../filter-manager/metadata-filter-manager'

interface MetadataHeaderRendererProps<TAggregationCategory extends string>
  extends CustomColumnHeaderRendererProps<TAggregationCategory> {
  readonly metadataAggregations: SessionMetadataAggregation[]
  readonly metadataSelections: MetadataFilter[]
  readonly onMetadataOptionsToggle: (groupID: string, values: string[]) => void
  readonly onClearMetadataSelections: () => void
  readonly isDrilldown?: boolean
}

export function metadataHeaderRenderer<TAggregationCategory extends string>({
  renderContent,
  clearAll,
  metadataAggregations = [],
  metadataSelections,
  onMetadataOptionsToggle,
  onClearMetadataSelections,
  isDrilldown = false
}: MetadataHeaderRendererProps<TAggregationCategory>) {
  function HeaderContent({
    isOpen,
    isClickable
  }: {
    readonly isOpen: boolean
    readonly isClickable: boolean
  }) {
    return renderContent({
      isClickable,
      isOpen,
      isFilterable: metadataAggregations.length > 0,
      isFiltered: metadataSelections.length > 0,
      onClear: () => {
        if (clearAll) {
          clearAll()
        }
        onClearMetadataSelections()
      }
    })
  }

  const filtersGroupConfigs: FilterConfigs = metadataAggregations
    .map(({ id, label, buckets }) => {
      const config: FilterConfig = {
        id,
        groupName: { id: label, defaultMessage: label },
        optionToString: ({ key }) => {
          return key.value
        },
        optionToID: ({ key }) => key.id,
        options: buckets
      }
      return {
        [id]: config
      }
    })
    .reduce((acc, curr) => ({ ...acc, ...curr }), {})

  const filtersSelections: FilterSelections = metadataAggregations
    .map(({ id }) => {
      const selectionGroup = metadataSelections.find(
        ({ fieldID }) => fieldID === id
      )
      return { [id]: (selectionGroup && selectionGroup.values) || [] }
    })
    .reduce((acc, curr) => ({ ...acc, ...curr }), {})

  const filtersGroupIDs = metadataAggregations.map(({ id }) => id)

  return metadataAggregations &&
    (doFiltersHaveOptions(filtersGroupConfigs, filtersGroupIDs) ||
      doFiltersHaveSelectionsOnGroups(filtersSelections, filtersGroupIDs)) ? (
    <Filters
      isDrilldown={isDrilldown}
      groupConfigs={{ ...filtersGroupConfigs }}
      selections={{
        ...filtersSelections
      }}
      groupIDs={[...filtersGroupIDs]}
      onGroupToggle={({ optionsAvailableForSelection, ...props }) => {
        const groupID = props.groupID as TAggregationCategory
        if (filtersGroupIDs.indexOf(groupID) > -1) {
          const currentSelections = filtersSelections[groupID]

          if (
            currentSelections.length === 0 ||
            currentSelections.length === optionsAvailableForSelection.length
          ) {
            onMetadataOptionsToggle(groupID, optionsAvailableForSelection)
          } else {
            onMetadataOptionsToggle(
              groupID,
              optionsAvailableForSelection.filter(
                (option) => currentSelections.indexOf(option) === -1
              )
            )
          }
        }
      }}
      onOptionToggle={({ optionID, ...props }) => {
        const groupID = props.groupID as TAggregationCategory
        if (filtersGroupIDs.indexOf(groupID) > -1) {
          onMetadataOptionsToggle(groupID, [optionID])
        }
      }}
    >
      {({ isOpen, isTransitioning }) => {
        return <HeaderContent isOpen={isOpen} isClickable={!isTransitioning} />
      }}
    </Filters>
  ) : (
    <HeaderContent isOpen={false} isClickable={false} />
  )
}
